import { ObjectHash, Theme } from '../../types/shared'
import { chartAlertIconsAsyncModule, chartEventIconsAsyncModule } from '../canvas-svg-icons/async-modules'
import { ChartEventBadgeType } from '../constants/common'
import { ChartEventBadgeState, IChartEventBadgeProps } from '../utils/chart-events-utils'
import { captureException } from '../utils/helpers'
import { ChartAlertIconName, ChartAlertIconState, IChartAlertIconProps } from './chartAlert/constants'
import { CHART_EVENT_BADGE_SIZE } from './constants'

type CanvasSvgImageCacheType = ObjectHash<{ image: HTMLImageElement; isLoaded?: boolean }, string>
let chartEventImagesCache: CanvasSvgImageCacheType | null = null
let chartAlertImagesCache: CanvasSvgImageCacheType | null = null

export type ChartEventBadgeCommonType = Omit<IChartEventBadgeProps, 'width'> & { iconName: ChartEventBadgeType }
export type ChartAlertIconCommonType = Omit<IChartAlertIconProps, 'width'> & { iconName: string }

function getSvgImageCacheKey(props: Record<string, string>) {
  return `${props.iconName}-${props.theme}-${props.state}`
}

export function fetchAndCacheImage({
  cacheKey,
  svgHTML,
  onComplete,
  cache,
}: {
  cacheKey: string
  svgHTML: string
  onComplete: () => void
  cache: CanvasSvgImageCacheType
}) {
  const handleImageResolve = () => {
    const hasSomeUnresolvedImages = Object.values(cache).some(({ isLoaded }) => isLoaded === undefined)
    if (!hasSomeUnresolvedImages) {
      onComplete()
    }
  }

  const img = new Image()
  img.onload = () => {
    cache[cacheKey].isLoaded = true
    handleImageResolve()
  }
  img.onerror = () => {
    cache[cacheKey].isLoaded = false
    handleImageResolve()
    captureException(new Error(`Image with cache key ${cacheKey} is invalid`), { extra: { src: img.src } })
  }
  img.src = `data:image/svg+xml,${encodeURIComponent(svgHTML)}`
  cache[cacheKey] = {
    image: img,
  }
}

interface FetchAndCacheIconsProps {
  onComplete: () => void
  iconNames: string[]
  iconStates: string[]
  cache: CanvasSvgImageCacheType
  importFn: () => Promise<{ iconsSvgByType: Record<string, (_: any) => string> }>
}

export async function fetchAndCacheIcons({
  iconNames,
  iconStates,
  onComplete,
  cache,
  importFn,
}: FetchAndCacheIconsProps) {
  // Currently node charts don't render badges and if the code below is executed on server it will crash because of missing file which is not transpiled
  if (typeof window === 'undefined') {
    return
  }

  // Temp workaround to prevent node charts from crashing
  const { importWithRetry } = await import('@finviz/website')
  const { iconsSvgByType } = await importWithRetry(importFn)

  iconNames.forEach((iconName) => {
    iconStates.forEach((iconState) => {
      Object.values(Theme).forEach((theme) => {
        const cacheKey = getSvgImageCacheKey({
          iconName,
          state: iconState,
          theme,
        })
        const svgHTML = iconsSvgByType[iconName]({
          state: iconState,
          theme,
          width: CHART_EVENT_BADGE_SIZE,
        })
        fetchAndCacheImage({
          cacheKey,
          svgHTML,
          onComplete,
          cache,
        })
      })
    })
  })
}

const CHART_EVENT_ICON_NAMES = Object.values(ChartEventBadgeType)
const CHART_EVENT_ICON_STATE = Object.values(ChartEventBadgeState)
export async function fetchAndCacheChartEventIcons({ onComplete }: { onComplete: () => void }) {
  if (chartEventImagesCache !== null) {
    // Early return if cache was already initialized
    return
  }
  chartEventImagesCache ??= {}
  return fetchAndCacheIcons({
    iconNames: CHART_EVENT_ICON_NAMES,
    iconStates: CHART_EVENT_ICON_STATE,
    importFn: chartEventIconsAsyncModule.importFn,
    cache: chartEventImagesCache,
    onComplete,
  })
}

const CHART_ALERT_ICON_NAMES = Object.values(ChartAlertIconName)
const CHART_ALERT_ICON_STATE = Object.values(ChartAlertIconState)
export async function fetchAndCacheChartAlertIcons({ onComplete }: { onComplete: () => void }) {
  if (chartAlertImagesCache !== null) {
    // Early return if cache was already initialized
    return
  }
  chartAlertImagesCache ??= {}
  return fetchAndCacheIcons({
    iconNames: CHART_ALERT_ICON_NAMES,
    iconStates: CHART_ALERT_ICON_STATE,
    importFn: chartAlertIconsAsyncModule.importFn,
    cache: chartAlertImagesCache,
    onComplete,
  })
}

function getBadgeImageFromCache({ cache, cacheKey }: { cache: CanvasSvgImageCacheType | null; cacheKey: string }) {
  const badgeImage = cache?.[cacheKey]
  if (badgeImage) {
    // Possible fix for https://finvizcom.sentry.io/issues/4737062163
    if (badgeImage.isLoaded && badgeImage.image.width > 0 && badgeImage.image.height > 0) {
      return badgeImage.image
    }
    return null
  }
  return undefined
}

export class CanvasSvgImg {
  static getChartEventIcon(props: ChartEventBadgeCommonType, onInitialLoad: () => void) {
    const badgeImage = getBadgeImageFromCache({ cache: chartEventImagesCache, cacheKey: getSvgImageCacheKey(props) })
    if (badgeImage === undefined) {
      void fetchAndCacheChartEventIcons({
        onComplete: onInitialLoad,
      })
    }

    return badgeImage
  }

  static getChartAlertIcon(props: ChartAlertIconCommonType, onInitialLoad: () => void) {
    const badgeImage = getBadgeImageFromCache({ cache: chartAlertImagesCache, cacheKey: getSvgImageCacheKey(props) })
    if (badgeImage === undefined) {
      void fetchAndCacheChartAlertIcons({
        onComplete: onInitialLoad,
      })
    }

    return badgeImage
  }
}
